import { gql } from '../../core/graphql-client.js';
import { auth } from '../../core/auth-store.js';
import { router } from '../../core/router.js';
import { toast } from '../../core/event-bus.js';
import type { Quarter, Saturday, AvailabilityStatus, Person, Role, PersonRoleLimit, PersonExclusion } from '../../types.js';

interface AvailEntry {
  personId: number;
  saturdayDate: string;
  status: AvailabilityStatus;
  note?: string | null;
}

export class AvailabilityPage extends HTMLElement {
  private saturdays: Saturday[] = [];
  private statuses: Map<string, AvailabilityStatus> = new Map();
  private notes: Map<string, string> = new Map();
  private original: Map<string, AvailabilityStatus> = new Map();
  private originalNotes: Map<string, string> = new Map();

  // Preferences
  private me: Person | null = null;
  private allRoles: Role[] = [];
  private allPeople: Person[] = [];
  private roleLimits: Map<string, number> = new Map(); // roleSlug → max
  private exclusions: PersonExclusion[] = [];

  private removeGuard: (() => void) | null = null;

  private onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.hasUnsavedChanges()) {
      e.preventDefault();
    }
  };

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie..."></loading-spinner>';
    this.loadData();
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.removeGuard = router.setGuard(() => {
      if (this.hasUnsavedChanges()) {
        return confirm('Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?');
      }
      return true;
    });
  }

  disconnectedCallback(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.removeGuard?.();
  }

  private hasUnsavedChanges(): boolean {
    for (const s of this.saturdays) {
      const cur = this.statuses.get(s.date) || 'UNKNOWN';
      const orig = this.original.get(s.date) || 'UNKNOWN';
      if (cur !== orig) return true;
      const curNote = this.notes.get(s.date) || '';
      const origNote = this.originalNotes.get(s.date) || '';
      if (curNote !== origNote) return true;
    }
    return false;
  }

  private async loadData(): Promise<void> {
    try {
      const personFields = `
        id displayName maxPerQuarter availabilityNotes
        roles { slug }
        roleLimits { role { slug } maxPerQuarter }
        exclusions { otherPersonId otherPersonDisplayName exclusionType scope }
      `;

      const [qData, personData, rolesData, pplData] = await Promise.all([
        gql.query<{ quarters: Quarter[] }>(`
          query { quarters { year quarterNum label isActive saturdays { date eventNote isSkipped sortOrder } } }
        `),
        auth.fetchCurrentPerson(personFields),
        gql.query<{ roles: Role[] }>(`
          query { roles { slug displayName sortOrder isAutoScheduled } }
        `),
        gql.query<{ people: Person[] }>(`
          query { people { id displayName } }
        `),
      ]);

      const quarter = qData.quarters.find(q => q.isActive);
      if (!quarter?.saturdays) {
        this.innerHTML = '<div class="page"><p>Brak aktywnego kwartału.</p></div>';
        return;
      }

      this.saturdays = quarter.saturdays.filter(s => !s.isSkipped);
      this.me = personData;
      this.allRoles = rolesData.roles.sort((a, b) => a.sortOrder - b.sortOrder);
      this.allPeople = pplData.people.filter(p => p.id !== this.me?.id);

      // Load role limits
      if (this.me?.roleLimits) {
        for (const rl of this.me.roleLimits) {
          this.roleLimits.set(rl.role.slug, rl.maxPerQuarter);
        }
      }
      this.exclusions = this.me?.exclusions ?? [];

      // Get my availability
      const aData = await gql.query<{ availabilityMatrix: { entries: AvailEntry[] } }>(`
        query($year: Int!, $qn: Int!) {
          availabilityMatrix(year: $year, quarterNum: $qn) {
            entries { personId saturdayDate status note }
          }
        }
      `, { year: quarter.year, qn: quarter.quarterNum });

      const myId = auth.effectivePersonId;
      for (const e of aData.availabilityMatrix.entries) {
        if (e.personId === myId) {
          this.statuses.set(e.saturdayDate, e.status);
          this.original.set(e.saturdayDate, e.status);
          if (e.note) {
            this.notes.set(e.saturdayDate, e.note);
            this.originalNotes.set(e.saturdayDate, e.note);
          }
        }
      }

      this.render(quarter.label);
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private render(quarterLabel: string): void {
    const formatDate = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'long' });
    };

    const myRoleSlugs = new Set(this.me?.roles?.map(r => r.slug) ?? []);

    this.innerHTML = `
      <div class="page avail-page">
        <div class="page-header">
          <h1>Dyspozycyjność — ${quarterLabel}</h1>
        </div>

        <div id="sat-list">
          ${this.saturdays.map(s => {
            const status = this.statuses.get(s.date) || 'UNKNOWN';
            const note = this.notes.get(s.date) || '';
            return `
              <div class="card avail-card" data-sdate="${s.date}">
                <div class="avail-row">
                  <div class="avail-date">
                    <strong>${formatDate(s.date)}</strong>
                    ${s.eventNote ? `<span class="avail-event">${s.eventNote}</span>` : ''}
                  </div>
                  <div class="seg-group">
                    <button class="seg-btn ${status === 'AVAILABLE' ? 'seg-active-yes' : ''}" data-status="AVAILABLE">Tak</button>
                    <button class="seg-btn ${status === 'UNAVAILABLE' ? 'seg-active-no' : ''}" data-status="UNAVAILABLE">Nie</button>
                    <button class="seg-btn ${status === 'UNKNOWN' ? 'seg-active-unknown' : ''}" data-status="UNKNOWN">?</button>
                  </div>
                  <textarea class="avail-note" placeholder="uwagi..." rows="1" data-sdate="${s.date}">${this.escapeAttr(note)}</textarea>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <div class="form-group avail-notes-group">
          <label for="availNotes">Uwagi</label>
          <textarea id="availNotes" rows="2" placeholder="Dodatkowe informacje dla admina...">${this.me?.availabilityNotes ?? ''}</textarea>
        </div>

        <details class="prefs-details">
          <summary class="prefs-toggle">Więcej ustawień</summary>
          <div class="prefs-content">
            <div class="form-group">
              <label>Limity ról (ile razy max na kwartał)</label>
              <div id="role-limits">
                ${this.allRoles.filter(r => r.isAutoScheduled && myRoleSlugs.has(r.slug)).map(r => `
                  <div class="limit-row">
                    <span class="limit-label">${r.displayName}</span>
                    <input type="number" min="0" max="13" class="role-limit-input"
                           data-role-slug="${r.slug}"
                           value="${this.roleLimits.get(r.slug) ?? ''}" placeholder="—">
                    <span class="limit-suffix">×</span>
                  </div>
                `).join('')}
              </div>
            </div>

            <div class="form-group">
              <label for="maxPerQuarter">Łączny max przypisań na kwartał</label>
              <input id="maxPerQuarter" type="number" min="0" max="20"
                     value="${this.me?.maxPerQuarter ?? ''}" placeholder="bez limitu">
            </div>
          </div>
        </details>

        <button class="fab" id="save-btn" title="Zapisz">Zapisz</button>
      </div>
      <style>
        .avail-page { padding-bottom: 80px; }
        .avail-card { margin-bottom: var(--space-sm); }
        .avail-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .avail-date { flex: 1; min-width: 0; }
        .seg-group { flex-shrink: 0; }
        .avail-event { font-size: .8rem; color: var(--color-on-surface-muted); margin-left: var(--space-xs); }
        .avail-note {
          width: 100%;
          box-sizing: border-box;
          margin-top: var(--space-xs);
          padding: 4px 8px;
          font-size: .8rem;
          font-family: inherit;
          border: 1px solid var(--color-surface-container);
          border-radius: var(--radius-sm, 4px);
          background: var(--color-surface);
          resize: none;
          overflow: hidden;
        }
        @media (min-width: 800px) {
          .avail-row { flex-wrap: nowrap; }
          .avail-note {
            width: auto;
            flex: 1;
            margin-top: 0;
            min-width: 120px;
          }
        }
        .seg-group { display: flex; gap: 2px; }
        .seg-btn {
          padding: 6px 12px;
          border: 1px solid var(--color-surface-container);
          background: var(--color-surface);
          cursor: pointer;
          font-size: .8rem;
          border-radius: var(--radius-sm, 4px);
          transition: all .15s;
        }
        .seg-active-yes { background: var(--color-success-bg, #d4edda); color: var(--color-success, #28a745); border-color: var(--color-success, #28a745); font-weight: 600; }
        .seg-active-no { background: var(--color-error-bg, #fdecea); color: var(--color-error); border-color: var(--color-error); font-weight: 600; }
        .seg-active-unknown { background: var(--color-surface-dim); font-weight: 600; }
        .avail-notes-group { margin-top: var(--space-md); }
        .avail-notes-group textarea { width: 100%; font-size: .85rem; padding: 8px; }
        .prefs-details { margin-top: var(--space-md); }
        .prefs-toggle {
          font-size: .8rem;
          color: var(--color-on-surface-muted);
          cursor: pointer;
          padding: var(--space-xs) 0;
        }
        .prefs-toggle:hover { color: var(--color-primary); }
        .prefs-content {
          margin-top: var(--space-sm);
          padding: var(--space-sm);
          font-size: .85rem;
          background: var(--color-surface);
          border-radius: var(--radius-md, 8px);
          border: 1px solid var(--color-surface-container);
        }
        .limit-row {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          margin-bottom: var(--space-xs);
        }
        .limit-label { flex: 1; font-size: .85rem; }
        .limit-row input { width: 60px; text-align: center; padding: 4px; font-size: .85rem; }
        .limit-suffix { font-size: .8rem; color: var(--color-on-surface-muted); }
        .fab {
          position: fixed;
          bottom: 70px;
          right: 16px;
          padding: 12px 24px;
          background: var(--color-primary);
          color: white;
          border: none;
          border-radius: var(--radius-md, 8px);
          font-size: .9rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,.2);
          z-index: 50;
        }
        .error-text { color: var(--color-error); }
      </style>
    `;

    // Status button events
    this.querySelector('#sat-list')!.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.seg-btn') as HTMLElement | null;
      if (!btn) return;
      const card = btn.closest('[data-sdate]') as HTMLElement;
      const sdate = card.dataset.sdate!;
      const status = btn.dataset.status as AvailabilityStatus;

      this.statuses.set(sdate, status);

      // Update button UI
      card.querySelectorAll('.seg-btn').forEach(b => {
        b.className = 'seg-btn';
      });
      if (status === 'AVAILABLE') btn.classList.add('seg-active-yes');
      if (status === 'UNAVAILABLE') btn.classList.add('seg-active-no');
      if (status === 'UNKNOWN') btn.classList.add('seg-active-unknown');
    });

    // Note textarea events + auto-resize
    const autoResize = (el: HTMLTextAreaElement) => {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    };
    this.querySelectorAll<HTMLTextAreaElement>('.avail-note').forEach(ta => {
      autoResize(ta);
      ta.addEventListener('input', () => {
        const sdate = ta.dataset.sdate!;
        this.notes.set(sdate, ta.value);
        autoResize(ta);
      });
    });

    // Save button
    this.querySelector('#save-btn')!.addEventListener('click', () => this.save());
  }

  private async save(): Promise<void> {
    // Build entries for ALL saturdays
    const entries = this.saturdays.map(s => ({
      saturdayDate: s.date,
      status: (this.statuses.get(s.date) || 'UNKNOWN').toLowerCase(),
      note: this.notes.get(s.date) || null,
    }));

    try {
      // Save availability
      await gql.query(`
        mutation($personId: Int!, $entries: [AvailabilityEntry!]!) {
          setAvailability(personId: $personId, entries: $entries) { personId saturdayDate status }
        }
      `, { personId: auth.effectivePersonId, entries });

      // Save preferences (max per quarter, availability notes)
      const maxInput = this.querySelector('#maxPerQuarter') as HTMLInputElement;
      const notesInput = this.querySelector('#availNotes') as HTMLTextAreaElement;
      const maxPerQuarter = maxInput.value ? Number(maxInput.value) : null;
      const availabilityNotes = notesInput.value.trim() || null;

      await gql.query(`
        mutation($id: Int!, $input: UpdatePersonInput!) {
          updatePerson(id: $id, input: $input) { id }
        }
      `, {
        id: auth.effectivePersonId,
        input: { maxPerQuarter, availabilityNotes },
      });

      // Save role limits
      const limitInputs = this.querySelectorAll('.role-limit-input') as NodeListOf<HTMLInputElement>;
      for (const input of limitInputs) {
        const roleSlug = input.dataset.roleSlug!;
        const val = input.value ? Number(input.value) : 0;
        await gql.query(`
          mutation($personId: Int!, $roleSlug: String!, $max: Int!) {
            setPersonRoleLimit(personId: $personId, roleSlug: $roleSlug, maxPerQuarter: $max) { id }
          }
        `, { personId: auth.effectivePersonId, roleSlug, max: val });
      }

      // Update originals
      for (const s of this.saturdays) {
        this.original.set(s.date, this.statuses.get(s.date) || 'UNKNOWN');
        this.originalNotes.set(s.date, this.notes.get(s.date) || '');
      }

      toast('Dyspozycyjność zapisana', 'success');
    } catch (e) {
      toast(`Błąd zapisu: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private escapeAttr(s: string): string {
    return s.replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }
}

customElements.define('availability-page', AvailabilityPage);
